import { useState } from "react";
import axios from "axios";
import { Loader2, Wand2 } from "lucide-react";

/**
 * Admin "Set Balance" tile — lets the owner fix any client's balance_usd
 * directly against the active API (preview OR live via `?live=1`), without
 * curl. Calls POST /api/admin/terminal/set-balance.
 */
export default function AdminSetBalance({ apiBase, token }) {
  const [code, setCode] = useState("");
  const [balance, setBalance] = useState("");
  const [clearPositions, setClearPositions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const amt = Number(balance);
      if (!code.trim() || !Number.isFinite(amt)) {
        throw new Error("Enter an access code and a numeric balance.");
      }
      const { data } = await axios.post(
        `${apiBase}/admin/terminal/set-balance`,
        {
          code: code.trim().toUpperCase(),
          balance_usd: amt,
          clear_positions: clearPositions,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data);
      setBalance("");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-10 border border-gold/30 bg-surface p-6"
      data-testid="admin-set-balance-card"
    >
      <div className="flex items-center gap-2">
        <Wand2 className="h-4 w-4 text-gold" />
        <p className="text-[10px] uppercase tracking-[0.28em] text-gold">
          Owner · Set terminal balance
        </p>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-white/50">
        Override a client's live balance (and clear any open positions). Use
        this to fix accounts you adjusted off-platform — no curl, no shell.
      </p>

      <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]">
        <div>
          <label
            htmlFor="setbal-code"
            className="text-[9px] uppercase tracking-[0.22em] text-white/40"
          >
            Access code
          </label>
          <input
            id="setbal-code"
            data-testid="admin-setbal-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="AUR-XXXXXXXX"
            className="mt-1 w-full border border-white/15 bg-ink px-3 py-2 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="setbal-amount"
            className="text-[9px] uppercase tracking-[0.22em] text-white/40"
          >
            New balance (USD)
          </label>
          <input
            id="setbal-amount"
            data-testid="admin-setbal-amount"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            placeholder="e.g. 250"
            inputMode="decimal"
            className="mt-1 w-full border border-white/15 bg-ink px-3 py-2 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
          />
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-setbal-submit"
            className="flex h-[38px] items-center justify-center gap-2 rounded-full bg-gold px-4 text-[10px] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-gold-light disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply override"}
          </button>
        </div>
      </form>

      <label className="mt-3 flex cursor-pointer items-center gap-2 text-[10px] text-white/55">
        <input
          type="checkbox"
          data-testid="admin-setbal-clear-positions"
          checked={clearPositions}
          onChange={(e) => setClearPositions(e.target.checked)}
          className="accent-gold"
        />
        Clear open positions (keeps equity equal to balance)
      </label>

      {error && (
        <p
          data-testid="admin-setbal-error"
          className="mt-3 text-[11px] text-[#FF003C]"
        >
          {error}
        </p>
      )}
      {result && (
        <p
          data-testid="admin-setbal-success"
          className="mt-3 text-[11px] text-[#26A69A]"
        >
          ✓ {result.code} balance set to ${Number(result.balance_usd).toLocaleString()}
          {result.cleared_positions ? " · positions cleared" : ""}.
        </p>
      )}
    </div>
  );
}
